import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clipboard,
  Clock,
  FileText,
  Heart,
  Info,
  MapPin,
  Navigation,
  Settings,
  Star,
  Target,
  TrendingUp,
  User,
  Users,
} from 'react-feather';
import type { ProjectConferenceTagWithMetadata } from '@trinity/chat/api/interfaces/projects';
import { User as UserIcon } from '@trinity/chat-components/components/icons/User.js';
import { Checkbox } from '@trinity/chat-components/components/presentation/Checkbox.js';
import { Spinner } from '@trinity/chat-components/components/presentation/Spinner.js';
import { useUpdateProjectTagMutation } from './hooks/useUpdateProjectTagMutation';
import styles from './style/Tag.Details.module.css';
import { ColorSelect } from './Tagging.ColorSelect';

type Props = {
  tag: ProjectConferenceTagWithMetadata;
};

export const TagDetails = ({ tag }: Props) => {
  const params = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const handleBackToTable = () => {
    navigate(`/projects/${params.projectId}/tags`);
  };

  const mutation = useUpdateProjectTagMutation(+params.projectId);

  const handleColorChange = (color: string) => {
    mutation.mutate({
      color,
      hidden: tag.hidden,
      includeSummary: tag.includeSummary,
      parentId: tag.parent?.id ?? null,
      tagId: tag.id,
      title: tag.base.name,
    });
  };

  const prompts = tag.base.customSummaryPrompt.length > 0 ? tag.base.customSummaryPrompt : [''];

  return (
    <div className={styles.root}>
      <div className={styles.wrap}>
        <div className={styles.main}>
          <BackButton onClick={handleBackToTable}>
            <ArrowLeft size={16} />
            Back to Table
          </BackButton>

          <div className={styles.content}>
            <div className={styles.headerSection}>
              <h1 className={styles.title}>Tag Details</h1>
            </div>

            <div className={styles.section}>
              <div className={styles.fields}>
                <Field
                  label="Tag Title"
                  value={tag.base.name} />
                <Field
                  label="Tag Category"
                  value={tag.base.tagCategory} />
                <Field
                  label="Date Published"
                  value={`N/A`} />
              </div>
              <div className={styles.fields}>
                <Field
                  label="Parent Tag"
                  value={tag.parent?.id ? `Parent ID: ${tag.parent.id}` : 'None'} />
                <Field
                  label="Coverage Count"
                  value={tag.metadata.taggedMomentsCount} />
                <Field
                  label="Published Status"
                  value={tag.hidden ? 'Hidden' : 'Published'} />
              </div>
              <div className={styles.fields}>
                <div className={styles.field}>
                  <div className={styles.label}>Color</div>
                  <div className={styles.value}>
                    {mutation.isPending
                      ? <div className={styles.spinner}>
                        <Spinner />
                      </div>
                      : <ColorSelect
                        color={tag.base.color}
                        onChange={handleColorChange} />}
                  </div>
                </div>
                <Field
                  label="Last Modified"
                  value={tag.base.lastModified ? new Date(tag.base.lastModified).toLocaleDateString() : 'N/A'} />
                <Field
                  label="Include in Summary"
                  value={tag.includeSummary ? 'Yes' : 'No'} />
              </div>
            </div>

            <div className={styles.headerSection}>
              <h2 className={styles.sectionTitle}>Patient Journey Publishing Details</h2>
            </div>
            <div className={styles.section}>
              <div className={styles.journey}>

                <DashboardBannerImage />

                <div className={styles.fields}>
                  <Field
                    value={tag.base.dashboardPublishType || 'N/A'}
                    label="Dashboard Publish Type" />
                  <Field
                    value={tag.ordinal?.toString() || 'N/A'}
                    label="Order" />
                  <PatientJourneyIcon
                    value={tag.base.patientJourneyIcon as IconName} />
                  <DashboardPublishSettings />
                </div>
              </div>
            </div>

            <div className={styles.headerSection}>
              <h2 className={styles.sectionTitle}>Custom Summary Prompt</h2>
            </div>
            <div className={styles.section}>
              <div className={styles.customSummaryInputs}>
                {prompts.map((prompt, index) => (
                  <div key={index} className={styles.customSummaryInputContainer}>
                    <TextArea
                      className={styles.customSummaryInput}
                      value={prompt}
                      placeholder="Define a custom prompt for AI summary generation..."
                      rows={3}
                      readOnly />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

TagDetails.displayName = 'Project.Tags.Tag.Details';

type FieldProps = {
  label: string;
  value: string | number | null | undefined;
};

const Field = (props: FieldProps) => {
  return (
    <div className={styles.field}>
      <div className={styles.label}>{props.label}</div>
      <div className={styles.value}>{props.value}</div>
    </div>
  );
};

type DashboardBannerImageProps = {
  value?: string;
};

const DashboardBannerImage = (props: DashboardBannerImageProps) => {
  const DefaultBannerImage = () => (
    <div className={styles.defaultBannerImage}>
      <UserIcon
        size={40}
        strokeWidth="1px"
        fill="#626262" />
    </div>
  );

  const BannerImage = () => (
    <img
      src={props.value}
      alt="Dashboard Banner"
      className={styles.bannerImage}
      draggable="false"
    />
  );

  return (
    <div className={styles.field}>
      <div className={styles.label}>
        {'Dashboard Banner Image'}
      </div>
      <div className={styles.value}>
        <div className={styles.editableBannerImage}>
          <div className={styles.bannerImageContainer}>
            {props.value ? <BannerImage /> : <DefaultBannerImage />}
          </div>
        </div>
      </div>
    </div>
  );
};

const PATIENT_JOURNEY_ICONS = {
  Heart: { component: Heart, label: 'Heart' },
  Activity: { component: Activity, label: 'Activity' },
  User: { component: User, label: 'User' },
  Users: { component: Users, label: 'Users' },
  MapPin: { component: MapPin, label: 'Location' },
  Calendar: { component: Calendar, label: 'Calendar' },
  Clock: { component: Clock, label: 'Time' },
  Clipboard: { component: Clipboard, label: 'Clipboard' },
  FileText: { component: FileText, label: 'Document' },
  TrendingUp: { component: TrendingUp, label: 'Trending Up' },
  Target: { component: Target, label: 'Target' },
  Navigation: { component: Navigation, label: 'Navigation' },
  Settings: { component: Settings, label: 'Settings' },
  Star: { component: Star, label: 'Star' },
  CheckCircle: { component: CheckCircle, label: 'Check Circle' },
  AlertCircle: { component: AlertCircle, label: 'Alert Circle' },
  Info: { component: Info, label: 'Info' },
} as const;


type IconName = keyof typeof PATIENT_JOURNEY_ICONS;

type PatientJourneyIconProps = {
  value: IconName;
};

const PatientJourneyIcon = (props: PatientJourneyIconProps) => {
  const selectedIcon = PATIENT_JOURNEY_ICONS[props.value] || PATIENT_JOURNEY_ICONS.Heart;

  const IconComponent = selectedIcon.component;

  return (
    <div className={styles.field}>
      <div className={styles.label}>
        {'Dashboard Publishing Settings'}
      </div>
      <div className={styles.value}>
        <div className={styles.selectedIcon}>
          <IconComponent size={20} />
          <span className={styles.iconLabel}>{selectedIcon.label}</span>
        </div>
      </div>
    </div>
  );
};

type DashboardPublishSettingsProps = {
  options?: string[];
};

const DashboardPublishSettings = (props: DashboardPublishSettingsProps) => {
  const defaultOptions = ['Publish in Dashboard', 'Publish in Full Page', 'None'];
  const options = props.options || defaultOptions;

  return (
    <div className={styles.field}>
      <div className={styles.label}>
        {'Dashboard Publishing Settings'}
      </div>
      <div className={styles.value}>
        <div className={styles.options}>
          {options.map((option, index) => (
            <div key={index} className={styles.option}>
              <Checkbox
                checked={false}
                disabled />
              <span className={styles.text}>{option}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const BackButton = styled.button`
  color: ${props => props.theme?.palette?.wl?.primary?.main || 'var(--pri-02)'};
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-semibold);
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const TextArea = styled.textarea`
  &:focus {
    border-color: ${props => props.theme?.palette?.wl?.primary?.main || 'var(--pri-02)'};
    outline: ${props => props.theme?.palette?.wl?.primary?.main || 'var(--pri-02)'};
  }
`;